import React from "react";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as R from "fp-ts/Record";
import { match } from "ts-pattern";

import { LeafIcon } from "src/assets/icons/leaf_icon";
import ExpertiseAndResourcesImg from "src/assets/images/domainexpertise.png";
import TeachingAndLearningImg from "src/assets/images/domainlearning.png";
import PurposeImg from "src/assets/images/domainpurpose.png";
import RelationshipsImg from "src/assets/images/domainrelationships.png";
import WellBeingImg from "src/assets/images/domainwellbeing.png";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { FSCIBrandedV2Report } from "src/pages/reports/fsci/v2/types";
import { isConstructHidden } from "src/config/hiddenConstructs";
import { isBenchmarkRanked } from "src/pages/reports/ranking";

import { meta } from "./meta";

/*
 * Types
 */

export type KeyGrowthOpportunitiesProps = {
    readonly report: FSCIBrandedV2Report;
};

/*
 * Main
 */

export const KeyGrowthOpportunities = (props: KeyGrowthOpportunitiesProps): React.ReactElement => {
    const { report } = props;
    const benchmarkRanked = isBenchmarkRanked(report.value.results);

    return (
        <div className="main-report-content-wrap">
            <PageTitle className="green-icon">
                <LeafIcon />
                Key Growth Opportunities
            </PageTitle>
            {benchmarkRanked ? (
                <Body>
                    Below are up to five areas of your school that represent key growth opportunities in regard to
                    creating and sustaining a flourishing school culture. These are the areas where your school’s score
                    is lowest relative to the FSCI benchmark shown for each construct in the domain sections of this
                    report. These areas can serve as potential focus points for long-range planning and school
                    improvement efforts.
                    <br />
                    <br />
                    Constructs are identified as Meaningful Strengths and Key Growth Opportunities by how far your
                    school’s score sits above or below the benchmark: the strengths are the constructs where your school
                    scores furthest above its benchmark, and the growth opportunities are those where it scores furthest
                    below. When fewer than five constructs fall below the benchmark, the growth opportunities include
                    the areas where your school sits closest to it.
                </Body>
            ) : (
                <Body>
                    Below are five areas of your school that represent key growth opportunities in regard to creating
                    and sustaining a flourishing school culture. These areas emerged from your school’s overall FSCI
                    profile (again, meaning these areas are <em>not</em> benchmarked against other Christian schools,
                    but emerged from your school’s data and are specific to your school’s unique profile). These areas
                    can serve as potential focus points for long-range planning and school improvement efforts.
                    <br />
                    <br />
                    Constructs are identified as Key Strengths and Key Growth Opportunities based on a standardized
                    measure of all schools’ scores, rather than the raw scores themselves. These strengths and growth
                    opportunities can be interpreted as your school’s unique profile among private Christian schools.
                </Body>
            )}
            <Separator />
            <div className="sub-item-wrap">
                {pipe(
                    report.value.results.keyGrowthOpportunities,
                    A.filter(({ construct }) => !isConstructHidden(construct)),
                    A.map(({ domain, construct }) => {
                        return (
                            <div className="an-opportunity-item s-and-g-opp" key={construct}>
                                <img
                                    alt={`${domain} key growth opportunity domain`}
                                    className="domain-image"
                                    src={match(domain)
                                        .with("expertiseAndResources", () => ExpertiseAndResourcesImg)
                                        .with("teachingAndLearning", () => TeachingAndLearningImg)
                                        .with("purpose", () => PurposeImg)
                                        .with("relationships", () => RelationshipsImg)
                                        .with("wellBeing", () => WellBeingImg)
                                        .exhaustive()}
                                />
                                {pipe(
                                    R.lookup(domain, meta),
                                    O.chain(R.lookup(construct)),
                                    O.map((m) => {
                                        return (
                                            <>
                                                <SectionTitle>{m.title}</SectionTitle>
                                                <Body>{m.prologue}</Body>
                                            </>
                                        );
                                    }),
                                    O.getOrElse(() => {
                                        return (
                                            <>
                                                <SectionTitle>Unknown</SectionTitle>
                                                <Body>Unknown</Body>
                                            </>
                                        );
                                    }),
                                )}
                            </div>
                        );
                    }),
                    (items) =>
                        items.length > 0 || !benchmarkRanked ? (
                            items
                        ) : (
                            <Body>
                                None of the constructs measured for your school this year scored below the FSCI
                                benchmark, so no key growth opportunities are listed. The domain sections of this report
                                show where your school sits closest to the benchmark.
                            </Body>
                        ),
                )}
            </div>
        </div>
    );
};
